import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../context/AuthContext'
import { httpClient } from '../api/httpClient'
import { useAstreaEnvironment } from './useAstreaEnvironment'
import { ASTREA_ENVIRONMENTS } from '../context/astreaEnvironments'

/**
 * Fetches the full `User` document (`server_app/models/user.js`) for the
 * currently logged-in session, exposing the permission flags used by the
 * legacy `mainController` (`app/main.js`) to conditionally show menu items:
 * `allowedToGenerateJwt`, `allowedToResetElastic`, `allowedToLogoutUsers`,
 * `allowedToManageBanner`, `allowedToManageSubscriptions`,
 * `allowedToLoadBackup` (commit 585169a, "feat(TA-938): add backup history
 * viewer").
 *
 * Ported 1:1 from `mainController`:
 *
 *   AuthService.getUser().then(function(user) {
 *     const allowed = user.allowedToGenerateJwt ?? false;
 *     $scope.allowedToGenerateJwt = user.urlAstrea != "Produção" || allowed;
 *     $scope.allowedToResetElastic = user.allowedToResetElastic;
 *     $scope.allowedToLogoutUsers = user.allowedToLogoutUsers;
 *     $scope.allowedToManageBanner = user.allowedToManageBanner;
 *     $scope.allowedToManageSubscriptions = user.allowedToManageSubscriptions;
 *     $scope.allowedToLoadBackup = user.allowedToLoadBackup;
 *   })
 *
 * `AuthService.getUser()` itself is `POST /users/getUser` with
 * `{ email: AuthService.getUserLogin() }` (see `app/register/
 * authService.js`), which is why this hook reads `getUserLogin()` from
 * `useAuth()` (task 009's `AuthContext`) rather than duplicating session
 * storage here — this hook only *reads* the already-established auth
 * context, it does not extend/modify it (see task 010's coordination notes
 * with the concurrent task 011 for why `AuthContext.jsx` itself is left
 * untouched by this task).
 *
 * `enabled: !!userLogin` guards against firing the request before a
 * session exists (e.g. on the `/login` screen, or the brief instant before
 * `AuthContext` restores a persisted session).
 *
 * Task 031 (`app/discountCoupon/discountCouponsController.js`) adds two more
 * flags using the exact same "not in Produção, or explicitly allowed" shape
 * as `allowedToGenerateJwt` above:
 *
 *   var RESTRICTED_COUPON_TYPES = {
 *     AUTOMATIC: "allowedToGenerateAutomaticCoupons",
 *     AMBASSADOR: "allowedToGenerateAmbassadorCoupons"
 *   };
 *   var restrictedCouponsAuthorizationValidator = function(userData, restrictedCouponType) {
 *     const isAllowed = userData[RESTRICTED_COUPON_TYPES[restrictedCouponType]] ?? false;
 *     return userData.urlAstrea != "Produção" || isAllowed;
 *   };
 *   AuthService.getUser().then(function (user) {
 *     $scope.allowedToGenerateAutomaticCoupons = restrictedCouponsAuthorizationValidator(user, "AUTOMATIC");
 *     $scope.allowedToGenerateAmbassadorCoupons = restrictedCouponsAuthorizationValidator(user, "AMBASSADOR");
 *   });
 *
 * Added here (the single shared `AuthService.getUser()` port) rather than
 * duplicated inside `client/src/modules/discountCoupon/`, for the same
 * reason `allowedToGenerateJwt` already lives here instead of in the
 * `customer` module that consumes it: every one of these flags comes off the
 * same `/users/getUser` document, and this hook is already the established
 * single call site for it (`AppShell.jsx`, `JwtLoginPage.jsx` indirectly via
 * `AppShell`'s gating). Note `userData[RESTRICTED_COUPON_TYPES[...]] ?? false`
 * in the legacy code reads `allowedToGenerateAutomaticCoupons`/
 * `allowedToGenerateAmbassadorCoupons` off the `User` document — but neither
 * field is declared on the Mongoose schema (`server_app/models/user.js`,
 * confirmed via `grep -n "allowedToGenerate" server_app/models/user.js`: no
 * match, unlike `allowedToGenerateJwt` which *is* declared there). This is a
 * pre-existing legacy gap (the two coupon flags can only ever be `true` if
 * someone hand-edits the Mongo document directly; the schema itself provides
 * no `default: false` -> the field is simply `undefined`, which `?? false`
 * still coerces to `false` correctly, so behavior is preserved even though
 * the schema gap means the intended "explicitly allow a user in Produção"
 * escape hatch has no UI/API to actually set it). Ported as-is (reading the
 * fields defensively via `?? false`), not "fixed", per this task's
 * instructions to document legacy gaps rather than correct them.
 */
export function useCurrentUser() {
  const { getUserLogin } = useAuth()
  const { urlAstrea } = useAstreaEnvironment()
  const userLogin = getUserLogin()

  const query = useQuery({
    queryKey: ['currentUser', userLogin],
    queryFn: async () => {
      const response = await httpClient.post('/users/getUser', { email: userLogin })
      return response.data
    },
    enabled: !!userLogin,
  })

  const user = query.data

  // `urlAstrea != "Produção"` in the legacy controller is a literal string
  // comparison against the Portuguese label of the current Astrea
  // environment (now fixed per deploy, see `useAstreaEnvironment`); reusing
  // the `ASTREA_ENVIRONMENTS.PRODUCTION` constant already defined in
  // `astreaEnvironments.js` instead of re-introducing the bare string here.
  const allowedToGenerateJwt = user
    ? urlAstrea !== ASTREA_ENVIRONMENTS.PRODUCTION || (user.allowedToGenerateJwt ?? false)
    : false

  // Port of `restrictedCouponsAuthorizationValidator` (see doc-comment above).
  const allowedToGenerateAutomaticCoupons = user
    ? urlAstrea !== ASTREA_ENVIRONMENTS.PRODUCTION || (user.allowedToGenerateAutomaticCoupons ?? false)
    : false
  const allowedToGenerateAmbassadorCoupons = user
    ? urlAstrea !== ASTREA_ENVIRONMENTS.PRODUCTION || (user.allowedToGenerateAmbassadorCoupons ?? false)
    : false

  return {
    user,
    isLoading: query.isLoading,
    allowedToGenerateJwt,
    allowedToResetElastic: user?.allowedToResetElastic ?? false,
    allowedToLogoutUsers: user?.allowedToLogoutUsers ?? false,
    allowedToManageBanner: user?.allowedToManageBanner ?? false,
    allowedToManageFeature: user?.allowedToManageFeature ?? false,
    allowedToManageSubscriptions: user?.allowedToManageSubscriptions ?? false,
    allowedToLoadBackup: user?.allowedToLoadBackup ?? false,
    allowedToGenerateAutomaticCoupons,
    allowedToGenerateAmbassadorCoupons,
    allowedToApplyCouponToATenant: user?.allowedToApplyCouponToATenant ?? false,
    allowedToUseRobots: user?.allowedToUseRobots ?? false,
    isSuperAdministrator: user?.isSuperAdministrator ?? false,
    isAdministrator: Boolean(user?.isAdministrator || user?.isSuperAdministrator),
  }
}

export default useCurrentUser
